import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Drawer,
  Empty,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from "antd";
import {
  CheckCircleOutlined,
  EditOutlined,
  EyeOutlined,
  LinkOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import Utilities from "../../../dependencies/custom/react-utilities";
import domainSettings from "../../../dependencies/custom/settings";

const { Text } = Typography;
const asList = (data) => (Array.isArray(data) ? data : data?.results || []);
const apiRows = (data) => (Array.isArray(data?.data) ? data.data : []);
const money = (value) =>
  `UGX ${Number(value || 0).toLocaleString("en-UG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
const loanColor = {
  RUNNING: "blue",
  Running: "blue",
  Active: "green",
  Arrears: "orange",
  ARREARS: "orange",
  Defaulted: "red",
  DEFAULTED: "red",
  Cleared: "green",
  CLEARED: "green",
  Paid: "green",
  Pending: "gold",
};

const PayrollSaccoTab = ({ profiles: suppliedProfiles }) => {
  const navigate = useNavigate();
  const urls = domainSettings.urls.finance;
  const [profiles, setProfiles] = useState(suppliedProfiles || []);
  const [connection, setConnection] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [accountProfile, setAccountProfile] = useState(null);
  const [accountSaving, setAccountSaving] = useState(false);
  const [verifyingId, setVerifyingId] = useState(null);
  const [loans, setLoans] = useState(null);
  const [disbursements, setDisbursements] = useState(null);
  const [loanDetails, setLoanDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [memberSearch, setMemberSearch] = useState("");
  const [members, setMembers] = useState(null);
  const [membersLoading, setMembersLoading] = useState(false);
  const [loanStatus, setLoanStatus] = useState("Running");
  const [linkMember, setLinkMember] = useState(null);
  const [linkSaving, setLinkSaving] = useState(false);
  const [accountForm] = Form.useForm();
  const [linkForm] = Form.useForm();

  const request = useCallback(
    async (method, url, data, errorText, quiet = false) => {
      const response = await Utilities.fetchWithAuth(
        method,
        url,
        data,
        navigate,
      );
      if (!response.ok) {
        if (!quiet) Utilities.showError(response, errorText);
        return null;
      }
      return response.data;
    },
    [navigate],
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [profileData, connectionData] = await Promise.all([
        request(
          "GET",
          urls.payrollProfiles,
          null,
          "Could not load payroll employees",
        ),
        request(
          "GET",
          urls.payrollSaccoConnection,
          null,
          "Could not check the SACCO connection",
          true,
        ),
      ]);
      if (profileData) setProfiles(asList(profileData));
      if (connectionData) setConnection(connectionData);
    } finally {
      setLoading(false);
    }
  }, [request, urls.payrollProfiles, urls.payrollSaccoConnection]);

  useEffect(() => {
    refresh();
  }, [refresh]);
  useEffect(() => {
    setProfiles(suppliedProfiles || []);
  }, [suppliedProfiles]);

  const editAccount = (profile) => {
    setAccountProfile(profile);
    accountForm.setFieldsValue({
      saccoAccountNo: profile.saccoAccountNo || "",
    });
  };

  const saveAccount = async () => {
    const values = await accountForm.validateFields();
    setAccountSaving(true);
    const url = urls.payrollProfileDetail.replace(
      ":id",
      accountProfile.profileId,
    );
    const result = await request(
      "PATCH",
      url,
      { saccoAccountNo: values.saccoAccountNo.trim() },
      "Could not save the SACCO account number",
    );
    setAccountSaving(false);
    if (!result) return;
    message.success("SACCO account number saved");
    setAccountProfile(null);
    await refresh();
  };

  const verifyAccount = async (profile, { open = false } = {}) => {
    if (!profile.saccoAccountNo) {
      editAccount(profile);
      return null;
    }
    setVerifyingId(profile.profileId);
    const url = urls.payrollProfileSaccoVerify.replace(
      ":id",
      profile.profileId,
    );
    const result = await request(
      "POST",
      url,
      {},
      "Could not verify this SACCO account",
    );
    setVerifyingId(null);
    if (!result) {
      await refresh();
      return null;
    }
    message.success("SACCO account verified");
    await refresh();
    const verifiedProfile = result.profile;
    if (open) setSelectedProfile(verifiedProfile);
    return verifiedProfile;
  };

  const loadEmployeeData = async (profile, refreshAccount = false) => {
    let current = profile;
    if (refreshAccount || !profile.saccoClientId) {
      current = await verifyAccount(profile, { open: true });
      if (!current) return;
    } else {
      setSelectedProfile(profile);
    }
    setLoans(null);
    setDisbursements(null);
    setLoanDetails(null);
    setLoanStatus("Running");
    const params = new URLSearchParams({ loanStatus: "Running" });
    if (refreshAccount) params.set("refresh", "true");
    const refreshQuery = refreshAccount ? "?refresh=true" : "";
    const [loanData, disbursementData] = await Promise.all([
      request(
        "GET",
        `${urls.payrollProfileSaccoLoans.replace(":id", current.profileId)}?${params}`,
        null,
        "Could not load this employee’s SACCO loans",
      ),
      request(
        "GET",
        `${urls.payrollProfileSaccoDisbursements.replace(":id", current.profileId)}${refreshQuery}`,
        null,
        "Could not load this employee’s SACCO disbursements",
      ),
    ]);
    if (loanData) setLoans(loanData);
    if (disbursementData) setDisbursements(disbursementData);
  };

  const loadLoansByStatus = async (status) => {
    if (!selectedProfile) return;
    setLoanStatus(status);
    setLoans(null);
    const base = urls.payrollProfileSaccoLoans.replace(
      ":id",
      selectedProfile.profileId,
    );
    const result = await request(
      "GET",
      `${base}?loanStatus=${encodeURIComponent(status)}`,
      null,
      `Could not load this employee’s ${status.toLowerCase()} loans`,
    );
    if (result) setLoans(result);
  };

  const loadLoanDetails = async (disbursementId) => {
    if (!selectedProfile || !disbursementId) return;
    setDetailsLoading(true);
    setLoanDetails(null);
    const base = urls.payrollProfileSaccoLoanDetails.replace(
      ":id",
      selectedProfile.profileId,
    );
    const result = await request(
      "GET",
      `${base}?disbursementId=${encodeURIComponent(disbursementId)}`,
      null,
      "Could not load the loan schedule and penalties",
    );
    setDetailsLoading(false);
    if (result) setLoanDetails(result);
  };

  const searchMembers = async () => {
    setMembersLoading(true);
    const query = memberSearch.trim()
      ? `?search=${encodeURIComponent(memberSearch.trim())}&perPage=100`
      : "?perPage=100";
    const result = await request(
      "GET",
      `${urls.payrollSaccoMembers}${query}`,
      null,
      "Could not load SACCO members",
    );
    setMembersLoading(false);
    if (result) setMembers(result);
  };

  const linkedProfileFor = (member) =>
    profiles.find(
      (profile) =>
        (profile.saccoClientId &&
          member.id &&
          String(profile.saccoClientId) === String(member.id)) ||
        (profile.saccoAccountNo &&
          member.account_no &&
          String(profile.saccoAccountNo).trim().toLowerCase() ===
            String(member.account_no).trim().toLowerCase()),
    );

  const openMemberLink = (member) => {
    setLinkMember(member);
    linkForm.resetFields();
  };

  const linkEmployeeToMember = async () => {
    const values = await linkForm.validateFields();
    const profile = profiles.find((row) => row.profileId === values.profileId);
    if (!profile || !linkMember?.account_no) return;

    setLinkSaving(true);
    const profileUrl = urls.payrollProfileDetail.replace(
      ":id",
      profile.profileId,
    );
    const saved = await request(
      "PATCH",
      profileUrl,
      { saccoAccountNo: String(linkMember.account_no) },
      "Could not link this employee to the SACCO member",
    );
    if (!saved) {
      setLinkSaving(false);
      return;
    }

    const verified = await request(
      "POST",
      urls.payrollProfileSaccoVerify.replace(":id", profile.profileId),
      {},
      "The account was saved, but SACCO verification failed",
    );
    setLinkSaving(false);
    if (!verified) {
      await refresh();
      return;
    }

    message.success(`${profile.staffName} linked to ${linkMember.name}`);
    setLinkMember(null);
    linkForm.resetFields();
    await refresh();
  };

  const linked = useMemo(
    () => profiles.filter((profile) => profile.saccoAccountNo).length,
    [profiles],
  );
  const verified = useMemo(
    () => profiles.filter((profile) => profile.saccoVerified).length,
    [profiles],
  );

  const profileColumns = [
    {
      title: "Employee",
      render: (_, profile) => (
        <Space direction="vertical" size={0}>
          <Text strong>{profile.staffName}</Text>
          <Text type="secondary">
            {profile.staffCode || profile.staffEmail}
          </Text>
        </Space>
      ),
    },
    {
      title: "SACCO account_no",
      dataIndex: "saccoAccountNo",
      render: (value) => value || <Text type="secondary">Not linked</Text>,
    },
    {
      title: "Verification",
      render: (_, profile) =>
        profile.saccoVerified ? (
          <Space direction="vertical" size={0}>
            <Tag color="green" icon={<CheckCircleOutlined />}>
              Verified
            </Tag>
            <Text type="secondary">
              {profile.saccoVerifiedAt
                ? dayjs(profile.saccoVerifiedAt).format("DD MMM YYYY HH:mm")
                : ""}
            </Text>
          </Space>
        ) : profile.saccoVerificationError ? (
          <Space direction="vertical" size={0}>
            <Tag color="red">Needs attention</Tag>
            <Text type="danger">{profile.saccoVerificationError}</Text>
          </Space>
        ) : (
          <Tag>Not verified</Tag>
        ),
    },
    {
      title: "SACCO member",
      render: (_, profile) => profile.saccoMember?.name || "—",
    },
    {
      title: "Actions",
      width: 260,
      render: (_, profile) => (
        <Space wrap>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => editAccount(profile)}
          >
            Account
          </Button>
          <Button
            size="small"
            icon={<SafetyCertificateOutlined />}
            loading={verifyingId === profile.profileId}
            disabled={!profile.saccoAccountNo}
            onClick={() => verifyAccount(profile)}
          >
            Verify
          </Button>
          <Button
            size="small"
            type="primary"
            icon={<EyeOutlined />}
            disabled={!profile.saccoAccountNo}
            onClick={() => loadEmployeeData(profile)}
          >
            Loans
          </Button>
        </Space>
      ),
    },
  ];

  const loanColumns = [
    {
      title: "Loan code",
      dataIndex: "loanCode",
      render: (value, row) => value || row.disbursementCode || "—",
    },
    { title: "Product", dataIndex: "productName" },
    {
      title: "Principal",
      dataIndex: "principal",
      align: "right",
      render: money,
    },
    {
      title: "Outstanding",
      dataIndex: "outstanding",
      align: "right",
      render: (value, row) => money(value ?? row.loanBalance),
    },
    {
      title: "Arrears",
      dataIndex: "totalArrears",
      align: "right",
      render: money,
    },
    {
      title: "Status",
      dataIndex: "loanStatus",
      render: (value) => (
        <Tag color={loanColor[value]}>{value || "Not provided"}</Tag>
      ),
    },
    {
      title: "",
      render: (_, row) => (
        <Button
          size="small"
          disabled={!row.disbursementId && !row.id}
          onClick={() => loadLoanDetails(row.disbursementId || row.id)}
        >
          View details
        </Button>
      ),
    },
  ];

  const disbursementColumns = [
    { title: "Loan", dataIndex: "disbursementCode" },
    { title: "Product", dataIndex: "productName" },
    { title: "Disbursed", dataIndex: "dateDisbursed" },
    {
      title: "Principal",
      dataIndex: "principal",
      align: "right",
      render: money,
    },
    {
      title: "Balance",
      dataIndex: "loanBalance",
      align: "right",
      render: money,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (value) => <Tag color={loanColor[value]}>{value}</Tag>,
    },
    {
      title: "",
      render: (_, row) => (
        <Button size="small" onClick={() => loadLoanDetails(row.id)}>
          View details
        </Button>
      ),
    },
  ];

  const scheduleRows = apiRows(loanDetails?.schedule);
  const penaltyRows = apiRows(loanDetails?.penalties);

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Row gutter={[16, 16]}>
        <Col xs={12} md={8}>
          <Card size="small">
            <Statistic
              title="Connection"
              value={connection?.configured ? "Configured" : "Not configured"}
              valueStyle={{
                color: connection?.configured ? "#3f8600" : "#cf1322",
              }}
            />
          </Card>
        </Col>
        <Col xs={12} md={8}>
          <Card size="small">
            <Statistic
              title="Employees linked"
              value={linked}
              suffix={`/ ${profiles.length}`}
            />
          </Card>
        </Col>
        <Col xs={12} md={8}>
          <Card size="small">
            <Statistic
              title="Accounts verified"
              value={verified}
              suffix={`/ ${linked}`}
            />
          </Card>
        </Col>
      </Row>

      <Tabs
        items={[
          {
            key: "employees",
            label: "Employee–SACCO links",
            children: (
              <Card
                size="small"
                extra={
                  <Button icon={<ReloadOutlined />} onClick={refresh}>
                    Refresh
                  </Button>
                }
              >
                <Table
                  rowKey="profileId"
                  loading={loading}
                  dataSource={profiles}
                  columns={profileColumns}
                  scroll={{ x: 1000 }}
                />
              </Card>
            ),
          },
          {
            key: "members",
            label: "SACCO members",
            children: (
              <Card size="small">
                <Space.Compact
                  style={{ width: "100%", maxWidth: 560, marginBottom: 16 }}
                >
                  <Input
                    value={memberSearch}
                    allowClear
                    placeholder="Search account number or member name"
                    onChange={(event) => setMemberSearch(event.target.value)}
                    onPressEnter={searchMembers}
                  />
                  <Button
                    type="primary"
                    icon={<SearchOutlined />}
                    loading={membersLoading}
                    onClick={searchMembers}
                  >
                    Search
                  </Button>
                </Space.Compact>
                {!members && (
                  <Empty
                    description="Search or load members supplied by SACCO"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  >
                    <Button onClick={searchMembers}>Load members</Button>
                  </Empty>
                )}
                {members && (
                  <Table
                    rowKey="id"
                    loading={membersLoading}
                    dataSource={apiRows(members)}
                    pagination={{ pageSize: 20 }}
                    columns={[
                      { title: "Account no.", dataIndex: "account_no" },
                      { title: "Name", dataIndex: "name" },
                      { title: "Branch", dataIndex: "branch_name" },
                      {
                        title: "Status",
                        dataIndex: "access_status",
                        render: (value) => (
                          <Tag color={value === "Active" ? "green" : "default"}>
                            {value}
                          </Tag>
                        ),
                      },
                      {
                        title: "Payroll employee",
                        render: (_, member) => {
                          const linkedProfile = linkedProfileFor(member);
                          return linkedProfile ? (
                            <Tag color="blue">{linkedProfile.staffName}</Tag>
                          ) : (
                            <Button
                              size="small"
                              icon={<LinkOutlined />}
                              disabled={member.access_status !== "Active"}
                              onClick={() => openMemberLink(member)}
                            >
                              Link employee
                            </Button>
                          );
                        },
                      },
                    ]}
                    scroll={{ x: 900 }}
                  />
                )}
              </Card>
            ),
          },
        ]}
      />

      <Modal
        title={`SACCO account — ${accountProfile?.staffName || ""}`}
        open={Boolean(accountProfile)}
        onCancel={() => setAccountProfile(null)}
        onOk={saveAccount}
        confirmLoading={accountSaving}
        okText="Save account"
      >
        <Alert
          type="info"
          showIcon
          message="Use the member’s SACCO account_no"
          description="Payroll uses an exact account_no match from /members. A changed account must be verified again."
          style={{ marginBottom: 16 }}
        />
        <Form form={accountForm} layout="vertical">
          <Form.Item
            name="saccoAccountNo"
            label="SACCO account number"
            rules={[
              {
                required: true,
                whitespace: true,
                message: "Enter the SACCO account number.",
              },
            ]}
          >
            <Input autoComplete="off" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Link employee to ${linkMember?.name || "SACCO member"}`}
        open={Boolean(linkMember)}
        onCancel={() => {
          setLinkMember(null);
          linkForm.resetFields();
        }}
        onOk={linkEmployeeToMember}
        confirmLoading={linkSaving}
        okText="Link and verify"
      >
        <Alert
          type="info"
          showIcon
          message={`SACCO account: ${linkMember?.account_no || "—"}`}
          description="This stores the SACCO account on the employee payroll profile and verifies the link against the SACCO members API. It does not create or repay a loan."
          style={{ marginBottom: 16 }}
        />
        <Form form={linkForm} layout="vertical">
          <Form.Item
            name="profileId"
            label="Payroll employee"
            rules={[{ required: true, message: "Select a payroll employee." }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Select employee"
              options={profiles
                .filter((profile) => !profile.saccoAccountNo)
                .map((profile) => ({
                  value: profile.profileId,
                  label: `${profile.staffName}${profile.staffCode ? ` · ${profile.staffCode}` : ""}`,
                }))}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title={`SACCO loans — ${selectedProfile?.staffName || ""}`}
        width={1100}
        open={Boolean(selectedProfile)}
        onClose={() => {
          setSelectedProfile(null);
          setLoans(null);
          setDisbursements(null);
          setLoanDetails(null);
        }}
        extra={
          selectedProfile && (
            <Button
              icon={<ReloadOutlined />}
              loading={verifyingId === selectedProfile.profileId}
              onClick={() => loadEmployeeData(selectedProfile, true)}
            >
              Re-verify & refresh
            </Button>
          )
        }
      >
        {selectedProfile && (
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            <Descriptions bordered size="small" column={{ xs: 1, md: 3 }}>
              <Descriptions.Item label="Employee">
                {selectedProfile.staffName}
              </Descriptions.Item>
              <Descriptions.Item label="account_no">
                {selectedProfile.saccoAccountNo}
              </Descriptions.Item>
              <Descriptions.Item label="SACCO member">
                {selectedProfile.saccoMember?.name || "Verified member"}
              </Descriptions.Item>
            </Descriptions>
            <Alert
              type="info"
              showIcon
              message="Loan information supplied by SACCO"
              description="Choose the exact SACCO loan status to view. Disbursements are shown separately and may use provider statuses such as Open."
            />
            <Space wrap>
              <Text strong>Loan status:</Text>
              <Select
                value={loanStatus}
                style={{ width: 180 }}
                onChange={loadLoansByStatus}
                options={[
                  "Running",
                  "Arrears",
                  "Expired",
                  "Defaulted",
                  "Cleared",
                ].map((value) => ({ value, label: value }))}
              />
            </Space>
            <Tabs
              items={[
                {
                  key: "loans",
                  label: `${loanStatus} loans (${loans?.total ?? loans?.count ?? apiRows(loans).length})`,
                  children: (
                    <Table
                      rowKey={(row) => row.disbursementId || row.id}
                      dataSource={apiRows(loans)}
                      columns={loanColumns}
                      locale={{
                        emptyText: `SACCO returned no ${loanStatus.toLowerCase()} loans for this employee.`,
                      }}
                      scroll={{ x: 1000 }}
                    />
                  ),
                },
                {
                  key: "history",
                  label: `Disbursements (${disbursements?.total ?? apiRows(disbursements).length})`,
                  children: (
                    <Table
                      rowKey="id"
                      dataSource={apiRows(disbursements)}
                      columns={disbursementColumns}
                      locale={{
                        emptyText:
                          "No loan disbursements were returned for this employee.",
                      }}
                      scroll={{ x: 900 }}
                    />
                  ),
                },
              ]}
            />

            {(detailsLoading || loanDetails) && (
              <Card
                loading={detailsLoading}
                title={
                  loanDetails
                    ? `SACCO loan details — ${loanDetails.disbursement?.disbursementCode || ""}`
                    : "Loading SACCO loan details"
                }
                extra={
                  loanDetails && (
                    <Button onClick={() => setLoanDetails(null)}>
                      Close details
                    </Button>
                  )
                }
              >
                {loanDetails && (
                  <Tabs
                    items={[
                      {
                        key: "schedule",
                        label: `Schedule (${loanDetails.schedule?.total ?? scheduleRows.length})`,
                        children: (
                          <Table
                            rowKey="id"
                            dataSource={scheduleRows}
                            pagination={false}
                            columns={[
                              { title: "#", dataIndex: "installmentNo" },
                              { title: "Due date", dataIndex: "dueDate" },
                              {
                                title: "Principal",
                                dataIndex: "principalExpected",
                                align: "right",
                                render: money,
                              },
                              {
                                title: "Interest",
                                dataIndex: "interestExpected",
                                align: "right",
                                render: money,
                              },
                              {
                                title: "Expected",
                                dataIndex: "totalExpected",
                                align: "right",
                                render: money,
                              },
                              {
                                title: "Paid",
                                dataIndex: "amountPaid",
                                align: "right",
                                render: money,
                              },
                              {
                                title: "Remaining",
                                dataIndex: "remainingBalance",
                                align: "right",
                                render: money,
                              },
                              {
                                title: "Status",
                                dataIndex: "status",
                                render: (value) => (
                                  <Tag color={loanColor[value]}>{value}</Tag>
                                ),
                              },
                            ]}
                            scroll={{ x: 950 }}
                          />
                        ),
                      },
                      {
                        key: "penalties",
                        label: `Penalties (${loanDetails.penalties?.total ?? penaltyRows.length})`,
                        children: (
                          <Table
                            rowKey="id"
                            dataSource={penaltyRows}
                            pagination={false}
                            columns={[
                              { title: "Date", dataIndex: "penaltyDate" },
                              {
                                title: "Days overdue",
                                dataIndex: "daysOverdue",
                              },
                              {
                                title: "Arrears",
                                dataIndex: "loanArrearsAmount",
                                align: "right",
                                render: money,
                              },
                              {
                                title: "Penalty",
                                dataIndex: "penaltyTotalCharges",
                                align: "right",
                                render: money,
                              },
                              {
                                title: "Outstanding",
                                dataIndex: "penaltyChargesOutstanding",
                                align: "right",
                                render: money,
                              },
                              {
                                title: "Status",
                                dataIndex: "status",
                                render: (value) => (
                                  <Tag color={loanColor[value]}>{value}</Tag>
                                ),
                              },
                            ]}
                          />
                        ),
                      },
                    ]}
                  />
                )}
              </Card>
            )}
          </Space>
        )}
      </Drawer>
    </Space>
  );
};

export default PayrollSaccoTab;
